import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  icon: Icon,
  disabled = false,
  hasSearch = false, // New prop for search functionality
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option =>
    (option.label || option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm(''); // Reset search term on selection
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm(''); // Reset search term when clicking outside
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedLabel = options.find(option => option.value === value)?.label || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className={`w-full h-12 px-4 pr-10 bg-white border-2 border-blue-200 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer transition-all duration-300 hover:border-blue-300 shadow-sm flex items-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {Icon && <Icon className="w-4 h-4 text-blue-500 mr-2" />}
        <span className="flex-1 text-left truncate">
          {selectedLabel}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-blue-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {hasSearch && (
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-20">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()} // Prevent closing dropdown when clicking search input
                />
              </div>
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value || index}
                className="px-4 py-2 cursor-pointer hover:bg-blue-50 text-gray-800"
                onClick={() => handleSelect(option.value)}
              >
                {option.label || option}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500">No options found.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;